import React, { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';
import { Alert, Button, Card, Input, message, Modal, Select, Space, Statistic, Table, Tabs, Tag, Tooltip, Typography, Upload } from 'antd';
import { InboxOutlined, ReloadOutlined, SearchOutlined, StopOutlined, UploadOutlined } from '@ant-design/icons';
import { UserContext } from '../../Contexts/UserContext';
import TradeBuilder, { enrichWithCurrentYear, useCurrentYearValues, HorizonTag, HORIZON_ORDER, HORIZON_LABELS } from './TradeBuilder';
import { loadTradeData, saveTradeData, useIsMobile } from './tradeStorage';
import './TradeCenter.css';

const { Title, Text } = Typography;
const STORAGE_KEY = 'aod.tradeCenter.values.v1';
const MARKS_KEY = 'aod.tradeCenter.marks.v1';
const POSITIONS = ['QB', 'RB', 'WR', 'TE', 'PICK'];
const MOBILE_PAGE_SIZE = 30;
const MOBILE_SORTS = {
  rank: (a, b) => (a.overallRank || 99999) - (b.overallRank || 99999),
  value: (a, b) => b.value - a.value,
  trend: (a, b) => b.trend30day - a.trend30day,
  name: (a, b) => a.name.localeCompare(b.name)
};
const REQUIRED_COLUMNS = ['name', 'team', 'position', 'age', 'fantasycalcid', 'sleeperid', 'mflid', 'value', 'overallrank', 'positionrank', 'trend30day'];

function parseDelimited(text) {
  const firstLine = text.split(/\r?\n/, 1)[0] || '';
  const delimiter = firstLine.includes(';') ? ';' : ',';
  const rows = [];
  let row = [];
  let field = '';
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (char === '"') {
      if (quoted && text[index + 1] === '"') {
        field += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === delimiter && !quoted) {
      row.push(field.trim());
      field = '';
    } else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && text[index + 1] === '\n') index += 1;
      row.push(field.trim());
      if (row.some((cell) => cell !== '')) rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }
  row.push(field.trim());
  if (row.some((cell) => cell !== '')) rows.push(row);
  return rows;
}

function parseRankingsCsv(text) {
  const rows = parseDelimited(text.replace(/^\uFEFF/, ''));
  if (rows.length < 2) throw new Error('The file has no player rows.');
  const headers = rows[0].map((header) => header.trim().toLowerCase());
  const missing = REQUIRED_COLUMNS.filter((column) => !headers.includes(column));
  if (missing.length) throw new Error(`Missing required columns: ${missing.join(', ')}`);

  const players = rows.slice(1).map((cells, index) => {
    const player = Object.fromEntries(headers.map((header, cellIndex) => [header, cells[cellIndex] || '']));
    const rawValue = Number(player.value);
    if (!player.name || !player.position || !Number.isFinite(rawValue)) return null;
    const value = rawValue * (player.position.toUpperCase() === 'TE' ? 2 : 1);
    return {
      ...player,
      key: String(player.fantasycalcid || player.mflid || `${player.name}-${index}`),
      fantasycalcId: player.fantasycalcid,
      sleeperId: player.sleeperid,
      mflId: player.mflid,
      value,
      age: Number.isFinite(Number(player.age)) ? Number(player.age) : null,
      overallRank: Number(player.overallrank) || null,
      positionRank: Number(player.positionrank) || null,
      trend30day: Number(player.trend30day) || 0
    };
  }).filter(Boolean);

  if (!players.length) throw new Error('No valid players found in the CSV.');
  return players;
}

function readSavedPlayers() {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!Array.isArray(value?.players)) return { players: [], uploadedAt: null, fileName: null, valueScaleVersion: 2 };
    if (value.valueScaleVersion === 2) return value;
    return {
      ...value,
      players: value.players.map((player) => ({
        ...player,
        value: Number(player.value) * (String(player.position).toUpperCase() === 'TE' ? 2 : 1)
      })),
      valueScaleVersion: 2
    };
  } catch (_) {
    return { players: [], uploadedAt: null, fileName: null, valueScaleVersion: 2 };
  }
}

const tradeDirection = (trade) => {
  if (trade.direction === 'incoming' || trade.direction === 'outgoing') return trade.direction;
  const mine = String(trade.myFranchiseID || '');
  const explicitDirection = String(trade.direction || trade.type || trade.status || '').toLowerCase();
  if (explicitDirection.includes('offer') && explicitDirection.includes('you')) return 'incoming';
  if (explicitDirection.includes('you') && explicitDirection.includes('offer')) return 'outgoing';
  const from = String(trade.from || trade.offeredBy || trade.offered_by || trade.franchise || trade.franchiseID || trade.franchise_id || '');
  const to = String(trade.to || trade.offeredTo || trade.offered_to || trade.franchise2 || trade.franchise2ID || '');
  if (mine && from === mine) return 'outgoing';
  if (mine && to === mine) return 'incoming';
  return 'unknown';
};

const normalizeTradeAssetId = (assetID) => {
  const id = String(assetID || '').trim();
  const pick = id.match(/^FP_(?:\d+_)?(\d{4})_(?:(?:early|mid|late)_(\d+)|(\d+))$/i);
  if (pick) {
    const round = pick[2] != null ? Number(pick[2]) + 1 : Number(pick[3]);
    return `FP_${pick[1]}_${round}`;
  }
  if (/^\d+$/.test(id)) return String(Number.parseInt(id, 10));
  return id.toLowerCase();
};

function TradeCenter() {
  const { userID } = useContext(UserContext);
  const fileInputRef = useRef(null);
  const isMobile = useIsMobile();
  const [mobileLimit, setMobileLimit] = useState(MOBILE_PAGE_SIZE);
  const [mobileSort, setMobileSort] = useState('rank');
  const marksSaveTimer = useRef(null);
  const saved = useMemo(readSavedPlayers, []);
  const [players, setPlayers] = useState(saved.players);
  const [uploadedAt, setUploadedAt] = useState(saved.uploadedAt);
  const [fileName, setFileName] = useState(saved.fileName);
  const [marks, setMarks] = useState(() => {
    try { return JSON.parse(localStorage.getItem(MARKS_KEY) || '{}'); } catch (_) { return {}; }
  });
  const [search, setSearch] = useState('');
  const [position, setPosition] = useState('ALL');
  const [watchFilter, setWatchFilter] = useState('ALL');
  const [horizonFilter, setHorizonFilter] = useState('ALL');
  const { currentYearValues, handleCurrentYearUpload } = useCurrentYearValues(userID);
  const enrichedPlayers = useMemo(() => enrichWithCurrentYear(players, currentYearValues.players), [players, currentYearValues.players]);
  const [tradeFilter, setTradeFilter] = useState('ALL');
  const [exposureFilter, setExposureFilter] = useState('ALL');
  const [exposureByMflID, setExposureByMflID] = useState({});
  const [exposureTotalLeagues, setExposureTotalLeagues] = useState(0);
  const [exposureErrors, setExposureErrors] = useState([]);
  const [exposureLoading, setExposureLoading] = useState(false);
  const [trades, setTrades] = useState([]);
  const [tradeErrors, setTradeErrors] = useState([]);
  const [tradeLoading, setTradeLoading] = useState(false);
  const [mflLoginVisible, setMflLoginVisible] = useState(false);
  const [mflLoginLoading, setMflLoginLoading] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ players, uploadedAt, fileName, valueScaleVersion: 2 }));
  }, [players, uploadedAt, fileName]);

  useEffect(() => {
    localStorage.setItem(MARKS_KEY, JSON.stringify(marks));
  }, [marks]);

  const marksRef = useRef(marks);
  marksRef.current = marks;

  // The database is the source of truth; local copies only seed an empty account and speed up first paint.
  useEffect(() => {
    let active = true;
    loadTradeData(userID, ['values', 'marks']).then((remote) => {
      if (!active) return;
      if (remote.values?.players?.length) {
        setPlayers(remote.values.players);
        setUploadedAt(remote.values.uploadedAt || null);
        setFileName(remote.values.fileName || null);
      } else if (saved.players.length) {
        saveTradeData(userID, 'values', { players: saved.players, uploadedAt: saved.uploadedAt, fileName: saved.fileName, valueScaleVersion: 2 }).catch(() => {});
      }
      if (remote.marks) setMarks(remote.marks);
      else if (Object.keys(marksRef.current).length) saveTradeData(userID, 'marks', marksRef.current).catch(() => {});
    }).catch(() => { if (active) message.warning('Could not load saved values from the server; showing this device\'s copy.'); });
    return () => { active = false; };
  }, [userID, saved]);

  useEffect(() => () => clearTimeout(marksSaveTimer.current), []);

  const loadExposure = useCallback(async (forceRefresh = false) => {
    setExposureLoading(true);
    try {
      const refreshParam = forceRefresh ? '&refresh=1' : '';
      const response = await fetch(`/api/trades/exposure?userID=${encodeURIComponent(userID || 1)}&year=2026${refreshParam}`);
      const data = await response.json();
      if (response.status === 401 && data.mflLoginRequired) {
        setMflLoginVisible(true);
        setExposureByMflID({});
        setExposureTotalLeagues(0);
        return;
      }
      if (!response.ok) throw new Error(data.message || 'Unable to load roster exposure.');
      const nextExposure = {};
      (data.response || []).forEach((item) => {
        nextExposure[normalizeTradeAssetId(item.playerID)] = item;
      });
      setExposureByMflID(nextExposure);
      setExposureTotalLeagues(Number(data.totalLeagues) || 0);
      setExposureErrors(data.errors || []);
    } catch (error) {
      setExposureErrors([{ error: error.message || 'Unable to load roster exposure.' }]);
    } finally {
      setExposureLoading(false);
    }
  }, [userID]);

  useEffect(() => {
    loadExposure();
  }, [loadExposure]);

  const handleUpload = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const nextPlayers = parseRankingsCsv(String(reader.result || ''));
        const timestamp = new Date().toISOString();
        setPlayers(nextPlayers);
        setUploadedAt(timestamp);
        setFileName(file.name);
        saveTradeData(userID, 'values', { players: nextPlayers, uploadedAt: timestamp, fileName: file.name, valueScaleVersion: 2 })
          .then(() => message.success(`Saved ${nextPlayers.length.toLocaleString()} player values.`))
          .catch((error) => message.error(`Loaded values, but they were not saved to the server: ${error.message}`));
      } catch (error) {
        message.error(error.message || 'Could not read the rankings CSV.');
      }
    };
    reader.onerror = () => message.error('Could not read the selected file.');
    reader.readAsText(file);
    return false;
  };

  const loadPendingTrades = async () => {
    setTradeLoading(true);
    setTradeErrors([]);
    try {
      const response = await fetch(`/api/trades/pending?userID=${encodeURIComponent(userID || 1)}&year=2026`);
      const data = await response.json();
      if (response.status === 401 && data.mflLoginRequired) {
        setMflLoginVisible(true);
        setTrades([]);
        return;
      }
      if (!response.ok) throw new Error(data.error || data.message || 'Unable to load pending trades.');
      setTrades(Array.isArray(data.response) ? data.response : []);
      setTradeErrors(data.errors || []);
    } catch (error) {
      message.error(error.message || 'Unable to load pending trades.');
    } finally {
      setTradeLoading(false);
    }
  };

  const connectMfl = async () => {
    setMflLoginLoading(true);
    try {
      const response = await fetch('/api/trades/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userID: userID || 1 })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'MFL login failed.');
      setMflLoginVisible(false);
      message.success('Connected to MFL. Loading pending offers…');
      await Promise.all([loadPendingTrades(), loadExposure()]);
    } catch (error) {
      message.error(error.message || 'Could not connect to MFL.');
    } finally {
      setMflLoginLoading(false);
    }
  };

  const toggleMark = (player, mark) => {
    const next = { ...marksRef.current, [player.key]: marksRef.current[player.key] === mark ? null : mark };
    marksRef.current = next;
    setMarks(next);
    clearTimeout(marksSaveTimer.current);
    marksSaveTimer.current = setTimeout(() => {
      saveTradeData(userID, 'marks', marksRef.current).catch(() => message.error('Could not save market marks to the server.'));
    }, 600);
  };

  const filteredPlayers = useMemo(() => enrichedPlayers.filter((player) => {
    const searchText = search.trim().toLowerCase();
    const matchesSearch = !searchText || player.name.toLowerCase().includes(searchText);
    const matchesPosition = position === 'ALL' || player.position === position;
    const mark = marks[player.key] || '';
    const matchesWatch = watchFilter === 'ALL' || mark === watchFilter;
    const exposureCount = exposureByMflID[normalizeTradeAssetId(player.mflId)]?.leagueCount || 0;
    const matchesExposure = exposureFilter === 'ALL'
      || (exposureFilter === 'ROSTERED' && exposureCount > 0)
      || (exposureFilter === 'UNROSTERED' && exposureLoading === false && exposureCount === 0)
      || (exposureFilter.startsWith('COUNT:') && exposureCount === Number(exposureFilter.slice(6)));
    return matchesSearch && matchesPosition && matchesWatch && matchesExposure && (horizonFilter === 'ALL' || player.horizon === horizonFilter);
  }), [enrichedPlayers, search, position, watchFilter, horizonFilter, marks, exposureFilter, exposureByMflID, exposureLoading]);

  useEffect(() => { setMobileLimit(MOBILE_PAGE_SIZE); }, [search, position, watchFilter, horizonFilter, exposureFilter, mobileSort]);

  const mobilePlayers = useMemo(() => (isMobile ? [...filteredPlayers].sort(MOBILE_SORTS[mobileSort]).slice(0, mobileLimit) : []), [isMobile, filteredPlayers, mobileSort, mobileLimit]);

  const renderMarkButtons = (player) => (
    <Space size={4}><Button size="small" type={marks[player.key] === 'BUY' ? 'primary' : 'default'} className={marks[player.key] === 'BUY' ? 'buy-active' : ''} onClick={() => toggleMark(player, 'BUY')}>Buy</Button><Button size="small" danger={marks[player.key] === 'SELL'} type={marks[player.key] === 'SELL' ? 'primary' : 'default'} onClick={() => toggleMark(player, 'SELL')}>Sell</Button><Button size="small" type={marks[player.key] === 'TRADEBAIT' ? 'primary' : 'default'} className={marks[player.key] === 'TRADEBAIT' ? 'bait-active' : ''} onClick={() => toggleMark(player, 'TRADEBAIT')}>Bait</Button></Space>
  );

  const valueColumns = [
    { title: 'Rank', dataIndex: 'overallRank', width: 72, sorter: (a, b) => (a.overallRank || 99999) - (b.overallRank || 99999) },
    { title: 'Player', dataIndex: 'name', sorter: (a, b) => a.name.localeCompare(b.name), render: (name, player) => <div className="trade-player"><Text strong>{name}</Text><Text type="secondary">{player.team && player.team !== 'null' ? player.team : '—'}{player.age ? ` · ${player.age}` : ''}</Text></div> },
    { title: 'Pos', dataIndex: 'position', width: 75, render: (value) => <Tag className={`position-tag pos-${String(value).toLowerCase()}`}>{value}</Tag> },
    { title: 'Dynasty value', dataIndex: 'value', width: 110, align: 'right', sorter: (a, b) => a.value - b.value, render: (value) => Number(value).toLocaleString() },
    { title: 'Dynasty rank', dataIndex: 'positionRank', width: 100, align: 'center', sorter: (a, b) => (a.positionRank || 99999) - (b.positionRank || 99999), render: (rank, player) => rank ? `${player.position}${rank}` : '—' },
    { title: 'This-year value', dataIndex: 'currentYearValue', width: 125, align: 'right', sorter: (a, b) => (a.currentYearValue || 0) - (b.currentYearValue || 0), render: (value, player) => value == null ? '—' : <Tooltip title={`Raw this-year CSV value: ${Number(player.rawCurrentYearValue).toLocaleString()}. Normalized to dynasty scale using players within ±2 ${player.position} ranks.`}>{Math.round(value).toLocaleString()}</Tooltip> },
    { title: 'This-year rank', dataIndex: 'currentYearPositionRank', width: 110, align: 'center', sorter: (a, b) => (a.currentYearPositionRank || 99999) - (b.currentYearPositionRank || 99999), render: (rank, player) => rank ? `${player.position}${rank}` : '—' },
    { title: 'Horizon', dataIndex: 'horizon', width: 150, filters: HORIZON_ORDER.map((value) => ({ text: HORIZON_LABELS[value], value })).concat([{ text: HORIZON_LABELS.MISSING, value: 'MISSING' }]), onFilter: (value, player) => player.horizon === value, render: (_, player) => <HorizonTag horizon={player.horizon} /> },
    { title: 'My Exposure', key: 'exposure', width: 125, align: 'right', sorter: (a, b) => (exposureByMflID[normalizeTradeAssetId(a.mflId)]?.leagueCount || 0) - (exposureByMflID[normalizeTradeAssetId(b.mflId)]?.leagueCount || 0), render: (_, player) => { const exposure = exposureByMflID[normalizeTradeAssetId(player.mflId)]; return exposureLoading && !exposureTotalLeagues ? <Text type="secondary">Loading…</Text> : <Tooltip title={exposureErrors.length ? 'Exposure may be incomplete because some leagues could not be checked.' : `${exposure?.leagueCount || 0} of ${exposureTotalLeagues} rostered leagues`}><span className="trade-exposure">{exposure?.leagueCount || 0} / {exposureTotalLeagues || '—'}{exposureTotalLeagues ? <Text type="secondary"> · {Math.round(exposure?.percentage || 0)}%</Text> : null}{exposureErrors.length ? '*' : ''}</span></Tooltip>; } },
    { title: '30d Trend', dataIndex: 'trend30day', width: 105, align: 'right', sorter: (a, b) => a.trend30day - b.trend30day, render: (trend) => <span className={trend >= 0 ? 'trend-up' : 'trend-down'}>{trend > 0 ? '+' : ''}{Number(trend).toLocaleString()}</span> },
    { title: 'Market', key: 'mark', width: 220, render: (_, player) => renderMarkButtons(player) }
  ];

  const visibleTrades = useMemo(() => trades.filter((trade) => {
    const direction = tradeDirection(trade);
    return tradeFilter === 'ALL' || (tradeFilter === 'INCOMING' && direction === 'incoming') || (tradeFilter === 'OUTGOING' && direction === 'outgoing');
  }), [trades, tradeFilter]);

  const playerByMflID = useMemo(() => {
    const map = new Map();
    players.forEach((player) => {
      const mflID = String(player.mflId || '').trim();
      if (!mflID || mflID.toUpperCase() === 'UNK') return;
      const normalizedID = normalizeTradeAssetId(mflID);
      const pickIsSpecific = /^FP_\d{4}_(?:early|mid|late)_\d+$/i.test(mflID);
      const previous = map.get(normalizedID);
      if (!previous || (!pickIsSpecific && previous.pickIsSpecific)) {
        map.set(normalizedID, { player, pickIsSpecific });
      }
      map.set(mflID.toLowerCase(), { player, pickIsSpecific });
    });
    return map;
  }, [players]);

  const getSideAssets = (assetIDs = []) => assetIDs.map((assetID) => {
    const match = playerByMflID.get(String(assetID).toLowerCase()) || playerByMflID.get(normalizeTradeAssetId(assetID));
    return { id: String(assetID), player: match?.player || null };
  });

  const getSideTotal = (assets) => assets.reduce((total, asset) => total + (asset.player?.value || 0), 0);

  const renderTradeSide = (assetIDs = []) => {
    const assets = getSideAssets(assetIDs);
    if (!assets.length) return <Text type="secondary">No assets</Text>;
    return <div className="trade-side-assets">
      {assets.map(({ id, player }) => (
        <div className="trade-side-asset" key={id}>
          <Text className="trade-side-asset-name">{player?.name || `MFL asset ${id}`}</Text>
          <Text className={player ? 'trade-asset-value' : 'trade-asset-unknown'}>{player ? Number(player.value).toLocaleString() : 'Value unavailable'}</Text>
        </div>
      ))}
      <div className="trade-side-total"><Text strong>Side total</Text><Text strong>{getSideTotal(assets).toLocaleString()}</Text></div>
    </div>;
  };

  const getNetValue = (trade) => {
    const myAssets = getSideAssets(trade.mySideAssetIDs || []);
    const theirAssets = getSideAssets(trade.theirSideAssetIDs || []);
    const missingValues = [...myAssets, ...theirAssets].some((asset) => !asset.player);
    return { value: getSideTotal(theirAssets) - getSideTotal(myAssets), missingValues };
  };

  const tradeColumns = [
    { title: 'League', dataIndex: 'leagueName', key: 'leagueName', render: (name, trade) => <a href={`https://www.myfantasyleague.com/2026/home/${trade.leagueID}`} target="_blank" rel="noopener noreferrer">{name || trade.leagueID}</a> },
    { title: 'Direction', key: 'direction', width: 150, render: (_, trade) => { const direction = tradeDirection(trade); return direction === 'incoming' ? <Tag color="blue">Offered to me</Tag> : direction === 'outgoing' ? <Tag color="purple">Offered by me</Tag> : <Tag>Direction unavailable</Tag>; } },
    { title: 'With', dataIndex: 'otherTeamName', key: 'otherTeamName', width: 180, render: (name, trade) => <div className="trade-team-name"><Text strong>{name || 'Unknown team'}</Text><Text type="secondary">{trade.otherFranchiseID || ''}</Text></div> },
    { title: 'Your side · you give', key: 'mySide', width: 260, render: (_, trade) => renderTradeSide(trade.mySideAssetIDs) },
    { title: 'Their side · you get', key: 'theirSide', width: 260, render: (_, trade) => renderTradeSide(trade.theirSideAssetIDs) },
    { title: 'Net value to you', key: 'netValue', width: 150, align: 'right', render: (_, trade) => { const net = getNetValue(trade); return <Tooltip title={net.missingValues ? 'Partial total: one or more assets were not found in the uploaded values CSV.' : 'Their-side value (you get) minus your-side value (you give).'}><span className={net.value >= 0 ? 'trade-net-positive' : 'trade-net-negative'}>{net.value > 0 ? '+' : ''}{net.value.toLocaleString()}{net.missingValues ? '*' : ''}</span></Tooltip>; } },
    { title: 'Trade ID', dataIndex: 'trade_id', key: 'trade_id', width: 100, render: (id) => id || '—' }
  ];

  const playerTab = (
    <>
      <div className="trade-toolbar">
        <Input allowClear prefix={<SearchOutlined />} placeholder="Search player name" value={search} onChange={(event) => setSearch(event.target.value)} className="trade-search" />
        {isMobile && <Select value={mobileSort} onChange={setMobileSort} className="trade-sort-filter" aria-label="Sort players">
          <Select.Option value="rank">Sort: Rank</Select.Option><Select.Option value="value">Sort: Value</Select.Option><Select.Option value="trend">Sort: 30d trend</Select.Option><Select.Option value="name">Sort: Name</Select.Option>
        </Select>}
        <Select value={position} onChange={setPosition} className="trade-position-filter" aria-label="Filter by position">
          <Select.Option value="ALL">All positions</Select.Option>
          {POSITIONS.map((item) => <Select.Option key={item} value={item}>{item === 'PICK' ? 'Draft picks' : item}</Select.Option>)}
        </Select>
        <Select value={watchFilter} onChange={setWatchFilter} className="trade-mark-filter" aria-label="Filter by market mark">
          <Select.Option value="ALL">All players</Select.Option>
          <Select.Option value="BUY">Buy list</Select.Option>
          <Select.Option value="SELL">Sell list</Select.Option>
          <Select.Option value="TRADEBAIT">Trade bait</Select.Option>
        </Select>
        <Select value={horizonFilter} onChange={setHorizonFilter} className="trade-mark-filter" aria-label="Filter by horizon">
          <Select.Option value="ALL">All horizons</Select.Option>
          {HORIZON_ORDER.map((horizon) => <Select.Option key={horizon} value={horizon}>{HORIZON_LABELS[horizon]}</Select.Option>)}
          <Select.Option value="MISSING">No comparison</Select.Option>
        </Select>
        <Select value={exposureFilter} onChange={setExposureFilter} className="trade-exposure-filter" aria-label="Filter by my roster exposure" disabled={!exposureTotalLeagues}>
          <Select.Option value="ALL">Any exposure</Select.Option>
          <Select.Option value="ROSTERED">Rostered in at least one</Select.Option>
          <Select.Option value="UNROSTERED">Not rostered</Select.Option>
          {Array.from({ length: exposureTotalLeagues }, (_, index) => index + 1).map((count) => (
            <Select.Option key={count} value={`COUNT:${count}`}>{count} of {exposureTotalLeagues} teams</Select.Option>
          ))}
        </Select>
        <Upload accept=".csv,text/csv" showUploadList={false} beforeUpload={handleUpload}>
          <Button icon={<UploadOutlined />}>{players.length ? 'Refresh values' : 'Upload values CSV'}</Button>
        </Upload>
        <Upload accept=".csv,text/csv" showUploadList={false} beforeUpload={handleCurrentYearUpload}>
          <Button icon={<UploadOutlined />}>{currentYearValues.fileName ? 'Refresh this-year CSV' : 'Upload this-year CSV'}</Button>
        </Upload>
        <Button icon={<ReloadOutlined />} loading={exposureLoading} onClick={() => loadExposure(true)}>Refresh exposure</Button>
      </div>
      {players.length ? (
        <>
          {exposureErrors.length > 0 && <Alert type="warning" showIcon message={`Exposure could not be checked in ${exposureErrors.length} league(s)`} description={exposureErrors.map((error) => error.leagueName ? `${error.leagueName}: ${error.error}` : error.error).join(' · ')} style={{ marginBottom: 12 }} />}
          <div className="trade-table-summary"><Text type="secondary">Showing {filteredPlayers.length.toLocaleString()} of {players.length.toLocaleString()} assets</Text><Text type="secondary">{fileName} · Updated {uploadedAt ? new Date(uploadedAt).toLocaleString() : '—'}</Text></div>
          {isMobile ? (
            <div className="trade-card-list">
              {mobilePlayers.map((player) => {
                const exposure = exposureByMflID[normalizeTradeAssetId(player.mflId)];
                return (
                  <div className="trade-card" key={player.key}>
                    <div className="trade-card-main">
                      <div className="trade-card-info"><Text strong>{player.name}</Text><Text type="secondary">{player.overallRank ? `#${player.overallRank} · ` : ''}{player.team && player.team !== 'null' ? player.team : '—'}{player.age ? ` · ${player.age}` : ''}{player.positionRank ? ` · ${player.position}${player.positionRank}` : ''}</Text></div>
                      <div className="trade-card-value"><Text strong>{Number(player.value).toLocaleString()}</Text>{player.currentYearValue != null && <Text type="secondary" style={{ fontSize: 12 }}>Year {Math.round(player.currentYearValue).toLocaleString()}</Text>}<span className={player.trend30day >= 0 ? 'trend-up' : 'trend-down'}>{player.trend30day > 0 ? '+' : ''}{Number(player.trend30day).toLocaleString()}</span></div>
                    </div>
                    <div className="builder-mobile-tags">
                      <HorizonTag horizon={player.horizon} />
                      {player.positionRank && <Tag>Dynasty {player.position}{player.positionRank}</Tag>}
                      {player.currentYearPositionRank && <Tag color="blue">Year {player.position}{player.currentYearPositionRank}</Tag>}
                    </div>
                    <div className="trade-card-foot">
                      <Tag className={`position-tag pos-${String(player.position).toLowerCase()}`}>{player.position}</Tag>
                      {exposureTotalLeagues > 0 && <span className="trade-exposure">{exposure?.leagueCount || 0}/{exposureTotalLeagues}</span>}
                      <div className="trade-card-marks">{renderMarkButtons(player)}</div>
                    </div>
                  </div>
                );
              })}
              {mobileLimit < filteredPlayers.length && <Button block size="large" onClick={() => setMobileLimit((limit) => limit + MOBILE_PAGE_SIZE)}>Show more ({(filteredPlayers.length - mobileLimit).toLocaleString()} left)</Button>}
            </div>
          ) : (
            <Table rowKey="key" size="small" columns={valueColumns} dataSource={filteredPlayers} pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'] }} scroll={{ x: 1250 }} />
          )}
        </>
      ) : (
        <div className="trade-empty"><InboxOutlined /><Title level={4}>Upload your FantasyCalc values</Title><Text type="secondary">Choose the semicolon-delimited FantasyCalc dynasty rankings CSV. Your latest upload and buy/sell marks are saved to your account.</Text><Button type="primary" icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()}>Choose CSV file</Button><input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) handleUpload(file); event.target.value = ''; }} /></div>
      )}
    </>
  );

  const pendingTradesTab = (
    <>
      <div className="trade-toolbar trade-offers-toolbar">
        <div><Title level={4}>Pending MFL offers</Title><Text type="secondary">{trades.length} offers across the leagues checked</Text></div>
        <Space>
          <Select value={tradeFilter} onChange={setTradeFilter} className="trade-mark-filter" aria-label="Filter pending trades">
            <Select.Option value="ALL">All offers</Select.Option><Select.Option value="INCOMING">Offered to me</Select.Option><Select.Option value="OUTGOING">Offered by me</Select.Option>
          </Select>
          <Button onClick={() => setMflLoginVisible(true)}>Connect MFL</Button>
          <Button icon={<ReloadOutlined />} loading={tradeLoading} onClick={loadPendingTrades}>Refresh offers</Button>
          <Tooltip title="MFL's trade-cancellation import format has not yet been verified. No offers will be changed until that API operation is confirmed."><Button danger disabled icon={<StopOutlined />}>Revoke all offers</Button></Tooltip>
        </Space>
      </div>
      {tradeErrors.length > 0 && <Alert type="warning" showIcon message={`${tradeErrors.length} league(s) could not be checked`} description={tradeErrors.map((error) => `${error.leagueName || error.leagueID}: ${error.error}`).join(' · ')} />}
      {isMobile ? (
        <div className="trade-card-list">
          {visibleTrades.map((trade) => {
            const direction = tradeDirection(trade);
            const net = getNetValue(trade);
            return (
              <div className="trade-card" key={trade.tradeKey}>
                <div className="trade-card-main">
                  <div className="trade-card-info"><a href={`https://www.myfantasyleague.com/2026/home/${trade.leagueID}`} target="_blank" rel="noopener noreferrer">{trade.leagueName || trade.leagueID}</a><Text type="secondary">With {trade.otherTeamName || 'Unknown team'}</Text></div>
                  <div className="trade-card-value">{direction === 'incoming' ? <Tag color="blue">To me</Tag> : direction === 'outgoing' ? <Tag color="purple">By me</Tag> : <Tag>Unknown</Tag>}<span className={net.value >= 0 ? 'trade-net-positive' : 'trade-net-negative'}>{net.value > 0 ? '+' : ''}{net.value.toLocaleString()}{net.missingValues ? '*' : ''}</span></div>
                </div>
                <div className="trade-card-sides">
                  <div><Text type="secondary">You give</Text>{renderTradeSide(trade.mySideAssetIDs)}</div>
                  <div><Text type="secondary">You get</Text>{renderTradeSide(trade.theirSideAssetIDs)}</div>
                </div>
              </div>
            );
          })}
          {!visibleTrades.length && !tradeLoading && <div className="trade-card-empty"><Text type="secondary">No pending offers loaded. Refresh to check your leagues.</Text></div>}
        </div>
      ) : (
        <Table rowKey="tradeKey" size="small" columns={tradeColumns} dataSource={visibleTrades} loading={tradeLoading} pagination={{ pageSize: 25 }} scroll={{ x: 1250 }} locale={{ emptyText: 'No pending offers loaded. Refresh to check your leagues.' }} />
      )}
      <Modal title="Connect your MFL account" visible={mflLoginVisible} onCancel={() => setMflLoginVisible(false)} onOk={connectMfl} confirmLoading={mflLoginLoading} okText="Connect">
        <Alert type="info" showIcon message="Authenticate with the server-configured MFL account" description="The server will use MFL_USER_ID and MFL_PASSWORD from its .env file. Your password is not sent from the browser or stored in browser data." />
      </Modal>
    </>
  );

  const buyCount = Object.values(marks).filter((mark) => mark === 'BUY').length;
  const sellCount = Object.values(marks).filter((mark) => mark === 'SELL').length;
  const baitCount = Object.values(marks).filter((mark) => mark === 'TRADEBAIT').length;

  return (
    <div className="trade-center">
      <header className="trade-header">
        <div><Text className="trade-eyebrow">DYNASTY MANAGEMENT</Text><Title level={2}>Trade Center</Title><Text type="secondary">Player values, market watchlists, and live MFL offers in one place.</Text></div>
        <div className="trade-header-stats"><Card size="small"><Statistic title="Players loaded" value={players.length} /></Card><Card size="small"><Statistic title="Buy targets" value={buyCount} valueStyle={{ color: '#16845b' }} /></Card><Card size="small"><Statistic title="Sell targets" value={sellCount} valueStyle={{ color: '#c43d52' }} /></Card><Card size="small"><Statistic title="Trade bait" value={baitCount} valueStyle={{ color: '#8752a5' }} /></Card></div>
      </header>
      <Tabs defaultActiveKey="values" onChange={(key) => { if (key === 'offers' && trades.length === 0) loadPendingTrades(); }} items={[
        { key: 'values', label: 'Player Values', children: playerTab },
        { key: 'offers', label: `MFL Offers${trades.length ? ` (${trades.length})` : ''}`, children: pendingTradesTab },
        { key: 'builder', label: 'Trade Builder', children: <TradeBuilder userID={userID} players={players} marks={marks} onConnectMfl={() => setMflLoginVisible(true)} currentYearValues={currentYearValues} handleCurrentYearUpload={handleCurrentYearUpload} /> }
      ]} />
    </div>
  );
}

export default TradeCenter;
